// vg-binding: P7.D-150
//
// apps/vendor/src/features/orders/detail/QCModal.tsx
//
// Phase 7 Wave 6 Task 21 — QC Pass / Fail modal.
//
// Pass mode: simple confirm dialog.
// Fail mode: requires a reason (mandatory; matches BE QC_REASON_REQUIRED).

import { useEffect, useState } from 'react';

import { Button } from '@printway/ui';

import { useTranslation } from '../../../lib/i18n/index.js';

interface Props {
  mode: 'pass' | 'fail';
  open: boolean;
  onCancel: () => void;
  onConfirm: (reason: string | undefined) => void | Promise<void>;
}

export function QCModal({ mode, open, onCancel, onConfirm }: Props): JSX.Element | null {
  const { t } = useTranslation('orders');
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (!open) setReason('');
  }, [open]);

  if (!open) return null;
  const isFail = mode === 'fail';
  const canConfirm = isFail ? reason.trim().length > 0 : true;

  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="qc-modal"
      /* dark-ok: modal scrim, translucent black reads correctly in both modes */
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
    >
      <div className="w-full max-w-md rounded-xl border border-pw-border bg-pw-bg p-6 shadow-xl">
        <h2 className="text-lg font-semibold text-pw-text">
          {isFail ? t('detail.qcModal.failTitle') : t('detail.qcModal.passTitle')}
        </h2>
        <p className="mt-2 text-sm text-pw-text-muted">
          {isFail
            ? t('detail.qcModal.failDescription')
            : t('detail.qcModal.passDescription')}
        </p>

        {isFail ? (
          <textarea
            data-testid="qc-fail-reason-input"
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={t('detail.qcModal.reasonPlaceholder')}
            className="mt-3 w-full rounded-md border border-pw-border bg-pw-bg p-2 text-sm text-pw-text focus:border-pw-primary focus:outline-none focus:ring-1 focus:ring-pw-primary"
          />
        ) : null}

        <div className="mt-4 flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            data-testid="qc-modal-cancel"
            onClick={onCancel}
          >
            {t('detail.qcModal.cancel')}
          </Button>
          <Button
            type="button"
            data-testid="qc-modal-confirm"
            onClick={() => {
              const r = isFail ? reason.trim() : undefined;
              void onConfirm(r);
            }}
            disabled={!canConfirm}
          >
            {isFail ? t('detail.qcModal.failConfirm') : t('detail.qcModal.passConfirm')}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default QCModal;
